<template>
  <div
    data-testid="auth-modal"
    class="relative min-h-screen flex flex-col items-center justify-start sm:justify-center bg-slate-900 pt-14 sm:pt-0"
  >
    <LocaleToggle
      class="absolute right-4 top-3"
      test-id-prefix="login-locale"
      enlarged-tap-target
    />
    <div
      data-testid="auth-context"
      class="w-full max-w-md mx-4 sm:mx-0 mb-6 text-center"
    >
      <h1 class="text-3xl font-bold text-white tracking-tight">Locktree</h1>
      <p class="mt-1 text-sm text-gray-300">
        {{ t("login.tagline") }}
        <span class="font-medium text-gray-100">{{
          t("login.seriousPlayers")
        }}</span>
      </p>
      <p class="mt-2 text-xs text-gray-400">
        {{ t("nav.aiSetup") }}
        <span class="text-gray-500" aria-hidden="true">·</span>
        {{ t("login.postflopSolver") }}
        <span class="text-gray-500" aria-hidden="true">·</span>
        {{ t("login.preflopSolutions") }}
      </p>
    </div>

    <div
      data-testid="auth-form"
      class="bg-slate-800 p-4 sm:p-8 rounded-lg shadow-xl w-full max-w-md mx-4 sm:mx-0"
    >
      <h2 class="text-2xl font-bold text-white text-center mb-6">
        {{ isRegisterMode ? t("login.createAccount") : t("login.signIn") }}
      </h2>

      <div class="space-y-3 mb-5">
        <button
          data-testid="auth-google-oauth-button"
          type="button"
          :disabled="authStore.isLoading"
          class="w-full min-h-touch-target py-2 px-4 bg-white hover:bg-gray-100 disabled:bg-gray-300 disabled:cursor-not-allowed text-slate-900 font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-slate-800"
          @click="startOAuth('google')"
        >
          {{
            isRegisterMode ? t("login.signUpGoogle") : t("login.continueGoogle")
          }}
        </button>
        <button
          data-testid="auth-apple-oauth-button"
          type="button"
          :disabled="authStore.isLoading"
          class="w-full min-h-touch-target py-2 px-4 bg-slate-950 hover:bg-black disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-md border border-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-slate-800"
          @click="startOAuth('apple')"
        >
          {{
            isRegisterMode ? t("login.signUpApple") : t("login.continueApple")
          }}
        </button>
        <p v-if="isRegisterMode" class="text-xs leading-snug text-gray-400">
          {{ t("login.oauthHelp") }}
        </p>
        <div class="flex items-center gap-3 text-xs text-gray-400">
          <div class="h-px flex-1 bg-slate-700" />
          <span class="text-center leading-snug">
            {{
              isRegisterMode
                ? t("login.createWithEmail")
                : t("login.signInWithEmail")
            }}
          </span>
          <div class="h-px flex-1 bg-slate-700" />
        </div>
      </div>

      <form class="space-y-4" @submit.prevent="handleSubmit">
        <div>
          <label
            for="auth-email"
            class="block text-gray-300 text-sm font-medium mb-2"
          >
            {{ t("login.email") }}
          </label>
          <input
            id="auth-email"
            v-model="email"
            data-testid="auth-email-input"
            type="email"
            required
            inputmode="email"
            autocomplete="email"
            class="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :placeholder="t('login.emailPlaceholder')"
          />
        </div>

        <div>
          <label
            for="auth-password"
            class="block text-gray-300 text-sm font-medium mb-2"
          >
            {{ t("login.password") }}
          </label>
          <input
            id="auth-password"
            v-model="password"
            data-testid="auth-password-input"
            type="password"
            required
            :autocomplete="isRegisterMode ? 'new-password' : 'current-password'"
            class="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :placeholder="t('login.password')"
          />
        </div>

        <div v-if="isRegisterMode">
          <label
            for="auth-confirm-password"
            class="block text-gray-300 text-sm font-medium mb-2"
          >
            {{ t("login.confirmPassword") }}
          </label>
          <input
            id="auth-confirm-password"
            v-model="confirmPassword"
            type="password"
            required
            autocomplete="new-password"
            class="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            :placeholder="t('login.confirmPasswordPlaceholder')"
          />
        </div>

        <div
          v-if="authStore.error"
          class="bg-red-500/20 border border-red-500 text-red-300 px-4 py-2 rounded-md text-sm"
        >
          {{ authStore.error }}
        </div>

        <button
          v-if="authStore.errorCode === 'email_not_verified'"
          type="button"
          :disabled="authStore.isLoading || !email"
          class="w-full py-2 px-4 border border-blue-500 text-blue-200 hover:bg-blue-500/10 disabled:border-slate-600 disabled:text-slate-500 disabled:cursor-not-allowed rounded-md text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800"
          @click="handleResendVerification"
        >
          {{ t("login.resendVerification") }}
        </button>

        <div
          v-if="localError"
          class="bg-red-500/20 border border-red-500 text-red-300 px-4 py-2 rounded-md text-sm"
        >
          {{ localError }}
        </div>

        <div
          v-if="successMessage"
          class="bg-green-500/20 border border-green-500 text-green-300 px-4 py-2 rounded-md text-sm"
        >
          {{ successMessage }}
        </div>

        <button
          data-testid="auth-signin-button"
          type="submit"
          :disabled="authStore.isLoading"
          class="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800"
        >
          <span
            v-if="authStore.isLoading"
            class="flex items-center justify-center"
          >
            <svg
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ t("login.processing") }}
          </span>
          <span v-else>
            {{ isRegisterMode ? t("login.createAccount") : t("login.signIn") }}
          </span>
        </button>
      </form>

      <div class="mt-6 text-center">
        <button
          data-testid="auth-toggle-mode-button"
          class="text-blue-400 hover:text-blue-300 text-sm transition-colors"
          @click="toggleMode"
        >
          {{ isRegisterMode ? t("login.haveAccount") : t("login.needAccount") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useAuthStore } from "../auth-store";
import { authClient, type OAuthProvider } from "../api/authClient";
import { validatePasswordPolicy } from "../utils/password-policy";
import { useI18n } from "../i18n";
import LocaleToggle from "./LocaleToggle.vue";

export default defineComponent({
  components: { LocaleToggle },
  emits: ["close", "success"],
  setup(_, { emit }) {
    const { t } = useI18n();
    const authStore = useAuthStore();
    const email = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const isRegisterMode = ref(false);
    const localError = ref("");
    const successMessage = ref("");

    const toggleMode = () => {
      isRegisterMode.value = !isRegisterMode.value;
      authStore.clearError();
      localError.value = "";
      successMessage.value = "";
      password.value = "";
      confirmPassword.value = "";
    };

    const handleSubmit = async () => {
      localError.value = "";
      successMessage.value = "";
      authStore.clearError();

      if (isRegisterMode.value) {
        if (password.value !== confirmPassword.value) {
          localError.value = t("login.passwordMismatch");
          return;
        }

        const passwordValidation = validatePasswordPolicy(password.value);
        if (!passwordValidation.isValid) {
          localError.value =
            passwordValidation.error || t("login.passwordRequirements");
          return;
        }

        const success = await authStore.register(email.value, password.value);
        if (success) {
          successMessage.value = t("login.accountCreated");
          isRegisterMode.value = false;
          password.value = "";
          confirmPassword.value = "";
        }
      } else {
        const success = await authStore.login(email.value, password.value);
        if (success) {
          emit("success");
          emit("close");
        }
      }
    };

    const handleResendVerification = async () => {
      localError.value = "";
      successMessage.value = "";
      const success = await authStore.resendEmailVerification(email.value);
      if (success) {
        successMessage.value = t("login.verificationSent");
      }
    };

    const startOAuth = (provider: OAuthProvider) => {
      authStore.clearError();
      localError.value = "";
      successMessage.value = "";
      authClient.startOAuth(provider);
    };

    return {
      t,
      authStore,
      email,
      password,
      confirmPassword,
      isRegisterMode,
      localError,
      successMessage,
      toggleMode,
      handleSubmit,
      handleResendVerification,
      startOAuth,
    };
  },
});
</script>
